import { useCallback, useMemo, useState } from 'react';

import { FilesFolders } from 'constants/files-folders-enum';
import { buildCanonicalIdType } from 'helpers/ids';
import { useGetIdTypeOptions } from 'hooks/resolve-id-doc-types';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useTranslation } from 'react-i18next';

import { DocumentUploadModal } from 'components/DocumentUploadModal/DocumentUploadModal';

export const CandidateDocumentUploadModal = ({
  candidate,
  company,
  visible,
  onUpdate = () => {},
  onClose = () => {},
  onAfterUpload = () => {},
}) => {
  const { t } = useTranslation();
  const { signUpload, createUpload, deleteUpload } = useApiUploadActions();
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const { options: rawOptions = [] } = useGetIdTypeOptions();
  const idTypeOptions = useMemo(
    () => rawOptions.map(o => ({ value: o.value, label: o.name })),
    [rawOptions],
  );

  const handleTypeChange = useCallback(
    val => setSelectedType(rawOptions.find(o => o.value === val) || null),
    [rawOptions],
  );
  const handleCountryChange = useCallback(val => setSelectedCountry(val), []);

  const handleUpload = async uploadData => {
    if (!selectedType) throw new Error('Please select a document type');
    if (!selectedCountry) throw new Error('Please select a country');
    const created = await createUpload(uploadData);
    onAfterUpload(created, selectedType, selectedCountry);
    setSelectedType(null);
    setSelectedCountry(null);
    onUpdate();
  };

  const handleDualUpload = async (uploadData, complementaryUploadData) => {
    if (!selectedType) throw new Error('Please select a document type');
    if (!selectedCountry) throw new Error('Please select a country');
    await createUpload(uploadData);
    await createUpload(complementaryUploadData);
    onAfterUpload(uploadData, complementaryUploadData, selectedType, selectedCountry);
    setSelectedType(null);
    setSelectedCountry(null);
    onUpdate();
  };

  return (
    <DocumentUploadModal
      visible={visible}
      onClose={onClose}
      onUpload={handleUpload}
      onDualUpload={handleDualUpload}
      title={t('admin.candidate.components.documentUpload.uploadDocument')}
      description={t('admin.candidate.components.documentUpload.uploadDocumentDescription')}
      selectedType={selectedType?.value}
      onTypeChange={handleTypeChange}
      selectedCountry={selectedCountry}
      onCountryChange={handleCountryChange}
      options={idTypeOptions}
      companyId={company?.id}
      modelParams={{
        candidate_id: candidate?.id,
        company_id: company?.id,
      }}
      metadata={{
        id_type: buildCanonicalIdType(selectedType?.value, selectedCountry),
      }}
      idDetails={{
        status: 'verified',
        verified_by: 'support',
        id_type: selectedType?.value,
        id_country_alpha2: selectedCountry,
        id_type_verified: true,
        id_country_verified: true,
      }}
      bucketFolder={FilesFolders.CandidatesApplicationDocuments}
      signUpload={signUpload}
      deleteUpload={deleteUpload}
      onlyUpload={false}
    />
  );
};
